import React, { useState, useCallback, useEffect } from 'react';
import { X } from 'lucide-react';
import { useAPI } from '../../../hooks/useAPI';
import EditProfileModal from './EditProfileModal';

const UserProfile = ({ user, onClose }) => {
  const { apiCall } = useAPI();
  const [myPosts, setMyPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [userData, setUserData] = useState(user);

  const loadMyPosts = useCallback(async () => {
    try {
      const data = await apiCall('/posts?category=feed');
      const userPosts = (data.posts || []).filter(post => post.user_id === user.id);
      setMyPosts(userPosts);
    } catch (err) {
      console.error('Error loading posts:', err);
    } finally {
      setLoading(false);
    }
  }, [user.id, apiCall]);

  useEffect(() => {
    loadMyPosts();
  }, [loadMyPosts]);

  const handleDeletePost = async (postId) => {
    if (window.confirm('Delete this post?')) {
      try {
        await apiCall(`/posts/${postId}`, { method: 'DELETE' });
        loadMyPosts();
      } catch (err) {
        alert(err.message);
      }
    }
  };

  const handleProfileUpdate = () => {
    // Refresh user data after edit
    loadMyPosts();
    setShowEditProfile(false);
  };

  const totalLikes = myPosts.reduce((sum, post) => sum + post.likes_count, 0);
  const totalComments = myPosts.reduce((sum, post) => sum + post.comments_count, 0);

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
        <div className="bg-white rounded-xl w-full max-w-4xl max-h-screen overflow-y-auto my-8">
          {/* Header Section */}
          <div className="sticky top-0 bg-white border-b p-6 z-10">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4 flex-1">
                {/* Profile Picture */}
                <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-purple-500 to-pink-500 flex-shrink-0">
                  {userData?.profile_picture ? (
                    <img 
                      src={userData.profile_picture} 
                      alt={userData.username} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white font-bold text-2xl">
                      {userData.username[0].toUpperCase()}
                    </div>
                  )}
                </div>

                {/* User Info */}
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-800">{userData.username}</h2>
                  <p className="text-sm text-gray-600">
                    Member since {new Date(userData.created_at).toLocaleDateString()}
                  </p>
                  
                  {userData?.bio && (
                    <p className="text-gray-700 mt-2">{userData.bio}</p>
                  )}

                  {/* Contact Info */}
                  <div className="flex flex-wrap gap-3 mt-3 text-sm">
                    {userData?.phone && (
                      <div className="flex items-center gap-1 text-gray-600">
                        <span>📞</span>
                        {userData.phone}
                      </div>
                    )}
                    {userData?.location && (
                      <div className="flex items-center gap-1 text-gray-600">
                        <span>📍</span>
                        {userData.location}
                      </div>
                    )}
                    {userData?.website && (
                      <a 
                        href={userData.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-blue-600 hover:underline"
                      >
                        <span>🌐</span>
                        Website
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Close and Edit Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => setShowEditProfile(true)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition font-semibold"
                >
                  ✏️ Edit Profile
                </button>
                <button 
                  onClick={onClose} 
                  className="text-gray-500 hover:text-gray-700 p-2"
                >
                  <X size={28} />
                </button>
              </div>
            </div>

            {/* Stats Section */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl p-4 text-white">
                <p className="text-2xl font-bold">{myPosts.length}</p>
                <p className="text-sm opacity-90">Total Posts</p>
              </div>
              <div className="bg-gradient-to-br from-red-500 to-pink-500 rounded-xl p-4 text-white">
                <p className="text-2xl font-bold">{totalLikes}</p>
                <p className="text-sm opacity-90">Total Likes</p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl p-4 text-white">
                <p className="text-2xl font-bold">{totalComments}</p>
                <p className="text-sm opacity-90">Total Comments</p>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-teal-500 rounded-xl p-4 text-white">
                <p className="text-2xl font-bold">{userData?.reputation || 0}</p>
                <p className="text-sm opacity-90">Reputation</p>
              </div>
            </div>
          </div>

          {/* My Posts Section */}
          <div className="p-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">My Posts</h3>
            
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading your posts...</p>
                </div>
              ) : myPosts.length === 0 ? (
                <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                  <p className="text-xl text-gray-600 mb-2">No posts yet</p>
                  <p className="text-sm text-gray-500">Start sharing to see your posts here!</p>
                </div>
              ) : (
                myPosts.map(post => (
                  <div 
                    key={post.id} 
                    className="bg-gray-50 rounded-xl p-5 border border-gray-200 hover:shadow-md transition"
                  >
                    {/* Post Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">
                          {post.category === 'found' ? '🟢' : 
                           post.category === 'missing' ? '🔴' : 
                           post.category === 'adoption' ? '❤️' : 
                           post.category === 'success_stories' ? '⭐' : '📍'}
                        </span>
                        <div>
                          <p className="font-bold text-gray-800">
                            {post.category.replace('_', ' ').toUpperCase()}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(post.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeletePost(post.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition"
                        title="Delete post"
                      >
                        🗑️
                      </button>
                    </div>

                    {/* Pet Info */}
                    {post.pet_name && (
                      <div className="bg-white p-3 rounded-lg mb-3 border-l-4 border-blue-500">
                        <p className="text-sm font-semibold text-blue-900">
                          🐾 Pet: {post.pet_name} {post.pet_type ? `(${post.pet_type})` : ''}
                        </p>
                      </div>
                    )}

                    {/* Post Content */}
                    <p className="text-gray-700 mb-3 line-clamp-3">{post.content}</p>

                    {/* Images */}
                    {post.media_urls && post.media_urls.length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {post.media_urls.slice(0, 3).map((url, idx) => (
                          <img
                            key={idx}
                            src={url}
                            alt={`Post ${idx}`}
                            className="w-full h-24 object-cover rounded-lg"
                          />
                        ))}
                        {post.media_urls.length > 3 && (
                          <div className="w-full h-24 bg-gray-300 rounded-lg flex items-center justify-center text-white font-bold">
                            +{post.media_urls.length - 3}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Location */}
                    {post.location_name && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                        <span>📍</span>
                        {post.location_name}
                      </div>
                    )}

                    {/* Contact Info */}
                    {post.contact_info && (
                      <div className="text-sm text-blue-600 mb-3">
                        <span>📞</span> {post.contact_info}
                      </div>
                    )}

                    {/* Stats & Status */}
                    <div className="flex items-center gap-4 text-sm border-t pt-3">
                      <span className="text-red-500">❤️ {post.likes_count}</span>
                      <span className="text-purple-500">💬 {post.comments_count}</span>
                      {post.status === 'resolved' && (
                        <span className="text-green-600 font-semibold ml-auto">✓ Reunited with Owner</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {showEditProfile && (
        <EditProfileModal
          user={userData}
          onClose={() => setShowEditProfile(false)}
          onUpdate={handleProfileUpdate}
        />
      )}
    </>
  );
};

export default UserProfile;